//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { GENERAL_MESSAGING_TIKTOK } from '@account-settings/components/rights-requests/constants/rightRequestConstants';
import { createNewTikTokConfiguration } from '@account-settings/components/rights-requests/utils';
import { IRightsOption } from '@legacy-components/rights-management/request-rights-model';
import { SocialNetwork } from '@models/SocialNetwork';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { getClientId } from '@redux/selectors/getClientId';
import { useRightsConfigurationsMutation } from '@uia/rights/hooks/useRightsConfigurationsMutation';
import {
  getIsBazaarvoiceNetworkSyndicationFrontendEnabled,
  getIsRightsRequestTermsAndConditionsEnabled,
  getIsTikTokMentionedRightsEnabled,
} from '@uia/rights/selectors/getUIARights';

interface UseTikTokConfigurationProps {
  rightsOptions?: IRightsOption[];
  socialNetwork?: SocialNetwork;
  cuid?: string;
  requestType?: string;
}

export const useTikTokConfiguration = ({
  cuid,
  requestType,
  rightsOptions,
  socialNetwork,
}: UseTikTokConfigurationProps) => {
  const queryClient = useQueryClient();
  const clientId = useSelector(getClientId);
  const isTikTokMentionedRightsEnabled = useSelector(
    getIsTikTokMentionedRightsEnabled,
  );
  const isRightsRequestTermsAndConditionsEnabled = useSelector(
    getIsRightsRequestTermsAndConditionsEnabled,
  );
  const isSyndicationAndTermsConditionsEnabled = useSelector(
    getIsBazaarvoiceNetworkSyndicationFrontendEnabled,
  );

  const { isPending: isCreating, mutate: createConfiguration } =
    useRightsConfigurationsMutation();

  const canCreateTikTokConfig = !!(
    rightsOptions?.length && isTikTokMentionedRightsEnabled
  );

  const hasTikTokConfig =
    rightsOptions?.some((option) => option.name === GENERAL_MESSAGING_TIKTOK) ??
    false;

  const shouldCreate = canCreateTikTokConfig && !hasTikTokConfig && !isCreating;

  const isTikTokContent = socialNetwork === SocialNetwork.TikTok;

  useEffect(() => {
    if (!shouldCreate) {
      return;
    }

    const tikTokConfig = createNewTikTokConfiguration(
      clientId,
      isSyndicationAndTermsConditionsEnabled,
      isRightsRequestTermsAndConditionsEnabled,
    );

    createConfiguration(tikTokConfig, {
      onSuccess: () => {
        if (cuid && requestType) {
          queryClient.invalidateQueries({
            queryKey: [
              QueryKeyConstants.UniversalImageActionsRightsConfigurations,
              cuid,
              requestType,
            ],
          });
        }
      },
    });
  }, [
    shouldCreate,
    clientId,
    isSyndicationAndTermsConditionsEnabled,
    isRightsRequestTermsAndConditionsEnabled,
    createConfiguration,
    queryClient,
    cuid,
    requestType,
  ]);

  const filteredRightsOptions = useMemo(() => {
    if (!rightsOptions) {
      return [];
    }

    if (isTikTokContent) {
      return rightsOptions
        .filter((option) => option.name === GENERAL_MESSAGING_TIKTOK)
        .slice(0, 1);
    } else {
      return rightsOptions.filter(
        (option) => option.name !== GENERAL_MESSAGING_TIKTOK,
      );
    }
  }, [rightsOptions, isTikTokContent]);

  return {
    filteredRightsOptions,
    hasTikTokConfiguration: hasTikTokConfig,
    isTikTokContent,
    isCreatingConfig: isCreating,
  };
};
